import React, { AnchorHTMLAttributes, ButtonHTMLAttributes, forwardRef, Ref } from "react";
import styled from "@emotion/styled";

import UIcon, { IconProps } from "../../../lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault } from "../../../lib/themes/colors";
import { Text } from "../../../styles/utility-components";

export type ButtonType = "primary" | "secondary" | "tertiary";
type ButtonStyle = "default" | "destructive" | "inverse";
type ButtonElementTypes = "button" | "a" | undefined; // undefined = Button
type ButtonShape = "circle" | "default";

type AnchorSpecificProps = {
  as: "a";
  href: string;
};
type ButtonSpecificProps = {
  as?: "button";
};

// Since we aren't using pointer-events: none when disabled as we have cursor: not-allowed instead,
// we need to handle disabled events ourself
const disabledOnWrapper = (
  e: React.MouseEvent,
  disabled: boolean = false,
  originalFunc: React.MouseEventHandler | undefined,
) => {
  if (disabled) {
    e.preventDefault();
    e.stopPropagation();
  } else if (originalFunc) {
    originalFunc(e);
  }
};

export type ButtonProps<T extends ButtonElementTypes> = {
  as?: T;
  children?: React.ReactNode;
  icon?: IconProps["icon"];
  iconProps?: Omit<IconProps, "icon">;
  buttonType?: ButtonType;
  buttonStyle?: ButtonStyle;
  blackText?: boolean;
  fullWidth?: boolean;
  justifyContent?: string;
  shape?: ButtonShape;
  disabled?: boolean;
  loading?: boolean;
} & (T extends "a"
  ? AnchorHTMLAttributes<HTMLAnchorElement> & AnchorSpecificProps
  : ButtonHTMLAttributes<HTMLButtonElement> & ButtonSpecificProps);

const ButtonInternal = <T extends ButtonElementTypes = "button">(
  {
    as,
    children,
    icon,
    iconProps,
    buttonType = "primary",
    buttonStyle = "default",
    blackText = false,
    disabled,
    loading,
    ...rest
  }: ButtonProps<T>,
  ref: Ref<T extends "a" ? HTMLAnchorElement : HTMLButtonElement>,
) => {
  if (!iconProps && buttonType === "secondary") {
    iconProps = { color: sysPrimaryDefault };
  }

  const isDisabled = disabled || loading;
  const isLink = as === "a";
  const href = isLink ? (rest as unknown as AnchorSpecificProps).href : undefined;

  const iconKey = loading ? "spinner" : icon;

  return (
    <ButtonWrapper
      as={as}
      buttonType={buttonType}
      buttonStyle={buttonStyle}
      buttonOnly={!children}
      {...(as !== "a" && {
        type: "button", // Default is submit which we don't want
      })}
      {...(rest as object)}
      ref={ref as Ref<HTMLButtonElement>}
      href={href}
      // Below is all related to disabled
      disabled={isLink ? undefined : isDisabled} // Prevent disabled from being set on anchor elements (not a valid attribute)
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : rest.tabIndex} // Prevents focus on disabled links
      // Add more events below as needed
      onClick={(e) => disabledOnWrapper(e, isDisabled, rest.onClick)}
      onPointerDown={(e) => disabledOnWrapper(e, isDisabled, rest.onPointerDown)}
    >
      {iconKey && <UIcon icon={iconKey} size={loading ? 12 : 16} {...iconProps} className="icon" />}
      {typeof children === "string" ? (
        <Text color={blackText ? "sysTextDefault" : "inherit"} ellipsis>
          {children}
        </Text>
      ) : (
        children
      )}
    </ButtonWrapper>
  );
};

const ButtonWrapper = styled.button<{
  buttonType: ButtonType;
  buttonStyle: ButtonStyle;
  buttonOnly: boolean;
  shape?: ButtonShape;
  fullWidth?: boolean;
  justifyContent?: string;
  loading?: boolean;
  href?: string;
}>(({ theme, buttonType, buttonStyle, buttonOnly, shape, fullWidth, justifyContent }) => ({
  all: "unset",
  height: 32,
  ...(shape === "circle" && { width: 32 }),
  padding: buttonOnly ? "0 8px" : "0 12px",
  borderRadius: shape === "circle" ? "50%" : 4,
  transition: "background-color 0.2s, border-color 0.2s",
  display: "flex",
  flexGrow: fullWidth ? 1 : 0,
  alignItems: "center",
  justifyContent: justifyContent ?? "center",
  gap: 8,
  cursor: "pointer",
  boxSizing: "border-box",

  ...(buttonType === "primary" && {
    backgroundColor: theme.colors.sysPrimaryDefault,
    color: theme.colors.sysTextWhite,
    border: "1px solid transparent", // Have so buttons all are the same height & width
    "&:hover, &[data-state='open']": {
      color: theme.colors.sysTextWhite,
      backgroundColor: theme.colors.sysPrimaryHover,
    },
    "&:active": {
      backgroundColor: theme.colors.sysPrimaryPressed,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.colors.sysPrimarySubtleAlt}`,
    },
    "&[aria-disabled='true']": {
      cursor: "not-allowed",
      backgroundColor: theme.colors.sysPrimarySubtleAlt,
    },

    ...(buttonStyle === "destructive" && {
      backgroundColor: theme.colors.sysDestructiveDefault,
      "&:hover, &[data-state='open']": {
        color: theme.colors.sysTextWhite,
        backgroundColor: theme.colors.sysDestructiveHover,
      },
      "&:active": {
        backgroundColor: theme.colors.sysDestructivePressed,
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysDestructiveSubtleAlt}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        backgroundColor: theme.colors.sysDestructiveSubtleAlt,
      },
    }),

    ...(buttonStyle === "inverse" && {
      backgroundColor: theme.colors.sysBackgroundDefault,
      color: theme.colors.sysTextDefault,
      "&:hover, &[data-state='open']": {
        color: theme.colors.sysTextDefault,
        backgroundColor: "#F7F7F8",
      },
      "&:active": {
        backgroundColor: theme.colors.sysBackgroundInactive,
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysPrimarySubtleAlt}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        color: theme.colors.sysTextInactive,
        backgroundColor: theme.colors.sysBackgroundDefault,
      },
    }),
  }),

  ...(buttonType === "secondary" && {
    backgroundColor: theme.colors.sysBackgroundDefault,
    color: theme.colors.sysTextDefault,
    border: `1px solid ${theme.colors.sysBorderPrimary}`,
    "&:hover, &[data-state='open']": {
      color: theme.colors.sysTextDefault,
      backgroundColor: theme.colors.sysPrimarySubtle,
      borderColor: theme.colors.sysPrimaryHover,
    },
    "&:active": {
      backgroundColor: theme.colors.sysPrimarySubtleAlt,
      borderColor: theme.colors.sysPrimaryPressed,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.colors.sysBorderPrimary}`,
    },
    "&[aria-disabled='true']": {
      cursor: "not-allowed",
      backgroundColor: theme.colors.sysBackgroundInactive,
      color: theme.colors.sysTextInactive,
      borderColor: theme.colors.sysBorderPrimary,
      "& .icon": {
        color: theme.colors.sysTextInactive,
      },
    },

    ...(buttonStyle === "destructive" && {
      borderColor: theme.colors.sysDestructiveDefault,
      "&:hover, &[data-state='open']": {
        color: theme.colors.sysTextDefault,
        borderColor: theme.colors.sysDestructiveHover,
        backgroundColor: theme.colors.sysDestructiveSubtle,
        "& .icon": {
          color: theme.colors.sysDestructiveHover,
        },
      },
      "&:active": {
        borderColor: theme.colors.sysDestructivePressed,
        backgroundColor: theme.colors.sysDestructiveSubtleAlt,
        "& .icon": {
          color: theme.colors.sysDestructivePressed,
        },
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysDestructiveSubtleAlt}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        backgroundColor: theme.colors.sysBackgroundInactive,
        borderColor: theme.colors.sysBorderPrimary,
        color: theme.colors.sysTextInactive,
        "& .icon": {
          color: theme.colors.sysTextInactive,
        },
      },
    }),

    ...(buttonStyle === "inverse" && {
      borderColor: theme.colors.sysBorderPrimary,
      backgroundColor: "transparent",
      color: theme.colors.sysTextWhite,
      "&:hover, &[data-state='open']": {
        color: theme.colors.sysTextWhite,
        borderColor: theme.colors.sysTextWhite,
        backgroundColor: "rgba(255, 255, 255, 0.08)",
      },
      "&:active": {
        borderColor: theme.colors.sysTextWhite,
        backgroundColor: "rgba(255, 255, 255, 0.12)",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysPrimarySubtle}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        backgroundColor: "transparent",
        borderColor: theme.colors.sysTextInactive,
        color: theme.colors.sysTextInactive,
        "& .icon": {
          color: theme.colors.sysTextInactive,
        },
      },
    }),
  }),

  ...(buttonType === "tertiary" && {
    backgroundColor: theme.colors.sysBackgroundDefault,
    color: theme.colors.sysPrimaryDefault,
    border: "1px solid transparent", // Have so buttons all are the same height
    "&:hover, &[data-state='open']": {
      color: theme.colors.sysPrimaryDefault,
      backgroundColor: theme.colors.sysPrimarySubtle,
    },
    "&:active": {
      backgroundColor: theme.colors.sysPrimarySubtleAlt,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.colors.sysPrimarySubtle}`,
    },
    "&[aria-disabled='true']": {
      cursor: "not-allowed",
      backgroundColor: theme.colors.sysBackgroundDefault,
      color: theme.colors.sysTextInactive,
    },

    ...(buttonStyle === "destructive" && {
      color: theme.colors.sysDestructiveDefault,
      "&:hover, &[data-state='open']": {
        color: theme.colors.sysDestructiveDefault,
        backgroundColor: theme.colors.sysDestructiveSubtle,
      },
      "&:active": {
        backgroundColor: theme.colors.sysDestructiveSubtleAlt,
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysDestructiveSubtleAlt}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        backgroundColor: theme.colors.sysBackgroundDefault,
        color: theme.colors.sysTextInactive,
      },
    }),

    ...(buttonStyle === "inverse" && {
      backgroundColor: "transparent",
      color: theme.colors.sysTextWhite,
      "&:hover, &[data-state='open']": {
        color: theme.colors.sysTextWhite,
        backgroundColor: "rgba(255, 255, 255, 0.08)",
      },
      "&:active": {
        backgroundColor: "rgba(255, 255, 255, 0.12)",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.colors.sysPrimarySubtle}`,
      },
      "&[aria-disabled='true']": {
        cursor: "not-allowed",
        backgroundColor: "transparent",
        color: theme.colors.sysTextInactive,
      },
    }),
  }),
}));

ButtonInternal.displayName = ButtonWrapper.displayName;

export type AnchorButtonProps = Omit<ButtonProps<"a">, "as"> &
  AnchorSpecificProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "type">;

export type ButtonButtonProps = Omit<ButtonProps<"button" | undefined>, "as"> &
  ButtonSpecificProps &
  ButtonHTMLAttributes<HTMLButtonElement>;

export type CombinedButtonProps = {
  (
    props: AnchorButtonProps & {
      ref?: React.Ref<HTMLAnchorElement>;
    },
  ): JSX.Element;
  (
    props: ButtonButtonProps & {
      ref?: React.Ref<HTMLButtonElement>;
    },
  ): JSX.Element;
};

/**
 *
 * @param as Which element to render this button as (button, a)
 * @param children Text or JSX to render inside the button
 * @param icon Untitled UI icon name to render inside the button
 * @param iconColorOverride A custom color to override the default color of the icon
 * @param buttonType The type of button to render (primary, secondary, tertiary)
 * @param buttonStyle The style of button to render (default, destructive, inverse)
 * @param blackText Whether the text should be forced to black
 * @param href If as="a", the href to render
 */
const Button = forwardRef(ButtonInternal) as CombinedButtonProps;

export default Button;
